// The shopper's answer to the cookie notice is a shop-wide fact, and this is the one reader that
// makes a refusal mean anything.
import { CONSENT_EVENT, readConsent } from '../consentAnswer';
import type { PixelEventName, PixelProvider, StorefrontEventContent, StorefrontEventName } from '@xshop/shared';
import { adVisitorId } from './adVisitorId';
import { loadPixelSetup, type PixelIdsByProvider } from './config';
import { googleAdsPixel, googleAnalyticsPixel, googleTagManagerPixel } from './google';
import { metaPixel } from './meta';
import { pinterestPixel } from './pinterest';
import { snapchatPixel } from './snapchat';
import { tiktokPixel } from './tiktok';
import type { BrowserPixel, PixelPayload } from './loader';

// The storefront's five funnel steps, in the vocabulary both the browser tags and the server speak.
const EVENT_NAMES: Record<StorefrontEventName, PixelEventName> = {
  page_view: 'PageView',
  product_view: 'ViewContent',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  purchase: 'Purchase'
};

// One browser tag per provider; adding a provider here is all it takes to start loading it.
const PIXELS: Record<PixelProvider, BrowserPixel> = {
  GOOGLE_ANALYTICS: googleAnalyticsPixel,
  GOOGLE_TAG_MANAGER: googleTagManagerPixel,
  GOOGLE_ADS: googleAdsPixel,
  FACEBOOK: metaPixel,
  TIKTOK: tiktokPixel,
  SNAPCHAT: snapchatPixel,
  PINTEREST: pinterestPixel
};

// What a storefront event can tell the pixels beyond its name, over and above the beacon's fields.
export interface PixelEventDetail {
  productId?: string;
  orderId?: string;
  orderNumber?: string;
  value?: number;
  currency?: string;
  numItems?: number;
  contents?: StorefrontEventContent[];
  contentName?: string;
}

let installed: PixelIdsByProvider | null = null;
const pending: Array<{ event: PixelEventName; payload: PixelPayload }> = [];

// Mints the deduplication id for one event. A purchase derives it from the order id, exactly as
// order.service.ts does server-side, so the platform sees one conversion instead of two.
export function pixelEventId(event: PixelEventName, detail: PixelEventDetail): string {
  if (event === 'Purchase' && detail.orderId) return `order_${detail.orderId}`;
  const random = Math.random().toString(36).slice(2, 10);
  return `${event}_${Date.now().toString(36)}_${random}`;
}

// Sends one event to every provider the store configured.
function fanOut(event: PixelEventName, payload: PixelPayload, ids: PixelIdsByProvider): void {
  for (const provider of Object.keys(ids) as PixelProvider[]) {
    try {
      PIXELS[provider].track(event, payload);
    } catch {
      // One broken vendor tag must never stop the others, and never surface into the store.
    }
  }
}

/**
 * Whether a shopper has refused third-party tags. A shop that never asks has no answer, and keeps
 * behaving exactly as it did — only an explicit refusal blocks, so turning the notice on is what
 * changes a shop's behaviour, not shipping this.
 */
const refused = (): boolean => readConsent() === 'rejected';

// Installs every configured tag, then flushes the events that fired while the config was loading.
let waiting = false;

export async function startPixels(): Promise<void> {
  if (installed) return;
  // A refusal is honoured before a single vendor script is fetched, and revisited if they change it.
  if (refused()) return void waitForConsent();
  const setup = await loadPixelSetup();
  installed = setup.ids;
  const identity = { externalId: adVisitorId() };
  for (const provider of Object.keys(setup.ids) as PixelProvider[]) {
    try {
      PIXELS[provider].install(setup.ids[provider] ?? [], identity);
    } catch {
      // A vendor that fails to install is simply absent; the storefront does not care.
    }
  }
  for (const queued of pending.splice(0)) fanOut(queued.event, queued.payload, installed);
}

// Waits for a shopper who refused to change their mind, then installs. Listens once and only once.
function waitForConsent(): void {
  if (typeof window === 'undefined' || waiting) return;
  waiting = true;
  window.addEventListener(CONSENT_EVENT, () => {
    waiting = false;
    if (!refused()) void startPixels();
  }, { once: true });
}

// Reports whether this shopper allows ad tags at all; a refusal keeps every pixel and its server copy off.
export const adsAllowed = (): boolean => !refused();

// Mints the id one funnel step is fired under, shared by the pixel and the server's copy, or none when ads are refused.
export const funnelEventId = (name: StorefrontEventName, detail: PixelEventDetail): string | undefined =>
  (refused() ? undefined : pixelEventId(EVENT_NAMES[name], detail));

// Fires one funnel step at every configured pixel under the given id; queued when the config has not landed yet.
export function firePixelEvent(name: StorefrontEventName, detail: PixelEventDetail, eventId: string): void {
  try {
    const event = EVENT_NAMES[name];
    const payload: PixelPayload = {
      eventId,
      value: detail.value,
      currency: detail.currency,
      contentId: detail.productId,
      orderId: detail.orderId,
      orderNumber: detail.orderNumber,
      numItems: detail.numItems,
      contents: detail.contents,
      contentName: detail.contentName
    };
    // A refusal drops the event outright: queueing it would fire the moment they ever accepted.
    if (refused()) return;
    if (!installed) pending.push({ event, payload });
    else fanOut(event, payload, installed);
  } catch {
    // Tracking is never allowed to surface an error into the store.
  }
}

/** What checkout hands the server for the conversion APIs to match the shopper on. */
export type PixelTracking = { fbp?: string; fbc?: string; ttp?: string; userAgent: string; sourceUrl: string; externalId: string };

// Reads the pixels' cookies, the browser and the page for the conversion APIs, or nothing for a shopper who refused tags.
export function pixelTrackingPayload(): PixelTracking | undefined {
  if (refused()) return undefined;
  const read = (name: string): string | undefined =>
    document.cookie.split('; ').find((part) => part.startsWith(`${name}=`))?.split('=')[1];
  return {
    fbp: read('_fbp'), fbc: read('_fbc'), ttp: read('_ttp'),
    userAgent: navigator.userAgent, sourceUrl: `${window.location.origin}${window.location.pathname}`, externalId: adVisitorId()
  };
}
